/**
 * How a bombing raid went — the fact the whole raid family is decided on, and
 * the one thing "the bombers survived" was never a good enough answer for.
 *
 * A raid has two sides of the same ledger and a mission reads whichever one it
 * was flown for:
 *
 * - **the bombers** — how many set out, how many are still up, and how many
 *   actually *got through*, which is neither of those. A Gotha that turned for
 *   home with a full rack survived and failed; one that was shot to pieces on
 *   the way out had already done the job. So "through" is counted from bombs
 *   released over the target, which the encounter records per aeroplane.
 * - **the targets** — how many of the raid's aiming points are down.
 *
 * **The pass mark is port-side and stated.** docs/game/missions.md records as an
 * open question whether the original's raid types "have distinct win conditions
 * (e.g. percentage of bombers/recon that must survive/be stopped)" and answers
 * it *"not discoverable from string dumps alone; would need actual play-testing
 * under DOSBox"*. So `RAID_SUCCESS_FRACTION` is a declared threshold with a name,
 * not a decoded rule — half the raid's targets down is a raid that worked, which
 * is the plainest reading a player can hold in their head while flying.
 *
 * What *is* the game's own is the shape either side is judged on. The manual
 * gives Escort a Bombing Raid as *"Your bombers are about to penetrate deep into
 * enemy territory to bomb strategic targets like RR yards, factories, and supply
 * dumps. It's your mission to see that they succeed."* and Stop a Bombing Raid
 * as *"Intercept enemy bombers who are trying to bomb your side."* — the first is
 * scored on the bombers getting through, the second on their not doing so.
 */

import { targetProfileOf } from "../combat/targets.ts";
import type { EncounterState } from "../combat/encounter.ts";

export const RAID_ROLES = ["escort", "intercept"] as const;

/** Which end of the raid the player is on: flying with it, or flying against it. */
export type RaidRole = (typeof RAID_ROLES)[number];

/**
 * Fraction of a raid's targets that have to be down for the raid itself to have
 * succeeded. Port-side — see the module note.
 */
export const RAID_SUCCESS_FRACTION = 0.5;

/** What a generator declares about the raid it built, so the outcome can be read back off the encounter. */
export interface RaidPlan {
  readonly role: RaidRole;
  /**
   * Everything carrying a rack — friendly on an escort, hostile on an
   * interception. Ids may name aeroplanes *or* the encounter's non-flying half:
   * a Zeppelin on its way in is a raider like any other, and is a target rather
   * than a flight only because it has no aerodynamics of its own.
   */
  readonly raiderIds: readonly string[];
  /** What they are carrying it to. */
  readonly targetIds: readonly string[];
}

interface RaiderReading {
  readonly alive: boolean;
  readonly bombsReleased: number;
  readonly bombsRemaining: number;
  /**
   * True once this raider is on the last leg of its route. Every generator in
   * this port puts the way home last, so that is the moment a bomber stops being
   * part of the raid — and it matters, because a heavy empties only as much of
   * its rack as the two or three seconds of a solution allow and then turns for
   * home with the rest still on it. Without this a raid could never be *over*,
   * and the escort mission that waits on that verdict would run for ever.
   */
  readonly homebound: boolean;
}

/** One raider's contribution, whichever list it lives in. */
function readRaider(encounter: EncounterState, id: string): RaiderReading | null {
  const flight = encounter.aircraft.find((entry) => entry.id === id);
  if (flight) {
    const route = flight.route;
    return {
      alive: flight.status === "flying",
      bombsReleased: flight.bombsReleased,
      bombsRemaining: flight.bombsRemaining,
      homebound: route !== null && (route.complete || route.index >= route.waypoints.length - 1),
    };
  }
  const airship = encounter.targets.find((entry) => entry.id === id);
  if (airship) {
    // An airship has no route to be on the last leg of: it holds one course all
    // the way in and is only finished when it is down or its rack is empty.
    return {
      alive: !airship.destroyed && !airship.burning,
      bombsReleased: airship.bombsReleased,
      bombsRemaining: airship.bombsRemaining,
      homebound: false,
    };
  }
  return null;
}

export interface RaidOutcome {
  readonly role: RaidRole;
  readonly bombersLaunched: number;
  /** Bomber aeroplanes still flying. */
  readonly bombersSurviving: number;
  /** Bombers that put at least one bomb down on the raid's targets. */
  readonly bombersThroughToTarget: number;
  readonly bombsReleased: number;
  readonly targetsTotal: number;
  readonly targetsDestroyed: number;
  /** True when the *raid* did its job, whichever side the player was on. */
  readonly raidSucceeded: boolean;
}

export const NO_RAID_OUTCOME: RaidOutcome = {
  role: "escort",
  bombersLaunched: 0,
  bombersSurviving: 0,
  bombersThroughToTarget: 0,
  bombsReleased: 0,
  targetsTotal: 0,
  targetsDestroyed: 0,
  raidSucceeded: false,
};

/** Reads the raid's state off the encounter. Pure, and safe to call every tick. */
export function raidOutcomeOf(plan: RaidPlan, encounter: EncounterState): RaidOutcome {
  const raiders = plan.raiderIds
    .map((id) => readRaider(encounter, id))
    .filter((entry): entry is RaiderReading => entry !== null);
  const targets = encounter.targets.filter((entry) => plan.targetIds.includes(entry.id));
  const targetsDestroyed = targets.filter((entry) => entry.destroyed || entry.burning).length;
  return {
    role: plan.role,
    bombersLaunched: plan.raiderIds.length,
    bombersSurviving: raiders.filter((entry) => entry.alive).length,
    bombersThroughToTarget: raiders.filter((entry) => entry.bombsReleased > 0).length,
    bombsReleased: raiders.reduce((sum, entry) => sum + entry.bombsReleased, 0),
    targetsTotal: targets.length,
    targetsDestroyed,
    raidSucceeded: targets.length > 0 && targetsDestroyed >= Math.ceil(targets.length * RAID_SUCCESS_FRACTION),
  };
}

/**
 * Whether the *player* got what he was sent for. An escort wants the raid to
 * succeed; an interception wants it not to.
 */
export function raidWentThePlayersWay(outcome: RaidOutcome): boolean {
  return outcome.role === "escort" ? outcome.raidSucceeded : !outcome.raidSucceeded;
}

/**
 * Whether the raid is **over** — the question a mission has to be able to answer
 * before it can be decided, and the one "have the targets fallen?" cannot.
 *
 * A raid ends when nothing that could still bomb can: every raider is down, has
 * an empty rack or has turned for home, and the last bomb has landed. Without
 * this a bombing raid that dropped everything and missed leaves the escort
 * mission running for ever — the targets are intact, so the objective never
 * completes, and the player is alive, so nothing else fails it either.
 */
export function raidIsOver(plan: RaidPlan, encounter: EncounterState): boolean {
  const raiders = plan.raiderIds
    .map((id) => readRaider(encounter, id))
    .filter((entry): entry is RaiderReading => entry !== null);
  if (raiders.length === 0) return true;
  if (raiders.some((entry) => entry.alive && entry.bombsRemaining > 0 && !entry.homebound)) return false;
  return !encounter.bombs.some((bomb) => plan.raiderIds.includes(bomb.bomberId));
}

/** The debrief's one-line reading of the raid. */
export function raidSummaryLine(outcome: RaidOutcome): string {
  const targets = `${outcome.targetsDestroyed} of ${outcome.targetsTotal} target${outcome.targetsTotal === 1 ? "" : "s"} destroyed`;
  if (outcome.role === "intercept") {
    const through = outcome.bombersThroughToTarget;
    return through === 0
      ? `The raid was turned back before it bombed — ${targets}.`
      : `${through} of ${outcome.bombersLaunched} bombers got their load away — ${targets}.`;
  }
  return `${outcome.bombersThroughToTarget} of ${outcome.bombersLaunched} bombers bombed, ${outcome.bombersSurviving} came back — ${targets}.`;
}

/** Whether a mission's targets are ground structures at all — i.e. whether a raid reading means anything. */
export function isRaidPlan(plan: RaidPlan | undefined): plan is RaidPlan {
  return plan !== undefined && plan.targetIds.length > 0;
}

/** The raid's targets that are structures rather than gasbags, for a generator's own assertion. */
export function groundTargetIdsOf(plan: RaidPlan, encounter: EncounterState): readonly string[] {
  return encounter.targets
    .filter((entry) => plan.targetIds.includes(entry.id) && targetProfileOf(entry.kind).targetClass === "ground")
    .map((entry) => entry.id);
}
